import React, { useCallback, useState, useMemo, useRef, useEffect } from "react";
import { useParams, useNavigate, matchPath, useLocation, Link } from "react-router-dom";
import { Breadcrumb, Table, Dropdown, Button, Collapse } from "antd";
import type { MenuProps } from "antd";
import dayjs from "dayjs";
import { useBrand } from "context/BrandContext";

import MediaPlayer from "components/media-player/media-player";
import ProductDetailsFeedback from "components/product-details-feedback/product-details-feedback";
import StatusIndicator from "components/status-indicator/status-indicator";
import DeviceAttachment from "components/device-attachment/device-attachment";
import DeviceStatusActivity from "components/device-status-activity/device-status-activity";
import DropdownArrow from "components/dropdown-arrow/dropdown-arrow";
import StructuredLocation from "components/structured-location/structured-location";
import { labelsFromRelationships } from "utils/location-hierarchy";

import { useGetInstalledProductByIdQuery } from "graphql/queries/get-installed-product-by-id.generated";
import { useGetInstalledDevicesByDeviceIdQuery } from "graphql/queries/get-installed-devices-by-device-id.generated";
import { useAiChatStatus, aiChatStatusInitialValue } from "state/reactive-vars/ai-chat-status-var";
import { useInstalledProductsFiltersStore } from "state/zustand-stores/installed-products-filters-store";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { createLabelForFilters, voidFnc } from "utils/helpers";
import { buildCheckboxMenuItems } from "utils/filter-helpers";
import {
	AiChatOrigin,
	AttachmentType,
	AttachmentTypePluralizedDisplayNames,
	MediaType,
	UserRoleValue
} from "constants/field-values";
import { DEVICE_STATUS_FILTER_OPTIONS } from "constants/constants";
import { getDeviceStatusKey, getDeviceStatusLabel } from "utils/device-status";
import routeNames from "constants/route-names";

const locationsTableColumns = [
	{
		title: "PROPERTY",
		dataIndex: "property",
		sorter: (a: any, b: any) => (a.property.name || "").localeCompare(b.property.name || ""),
		render: (property, record: any) => {
			const img = record?.photo_link ? (
				<img className="locations-table-photo" src={record.photo_link} alt="Device" />
			) : null;

			return (
				<>
					{img}
					{property.name}
				</>
			);
		}
	},
	{
		title: "LOCATION",
		dataIndex: "location",
		sorter: (a: any, b: any) => (a.location || "").localeCompare(b.location || ""),
		render: (location, record: any) => {
			const structuredLocationLabel = labelsFromRelationships(record);
			const locationLabel = location ? location : structuredLocationLabel ? null : "N/A";

			return (
				<div>
					<StructuredLocation labels={structuredLocationLabel} />
					{locationLabel}
				</div>
			);
		}
	},
	{
		title: "SERIAL #",
		dataIndex: "product_serial",
		sorter: (a: any, b: any) => (a.product_serial || "").localeCompare(b.product_serial || ""),
		render: serial => serial || "N/A"
	},
	{
		title: "INSTALL DATE",
		dataIndex: "installation_date",
		sorter: (a: any, b: any) => {
			const aTime = a.installation_date ? new Date(a.installation_date).getTime() : 0;
			const bTime = b.installation_date ? new Date(b.installation_date).getTime() : 0;
			return aTime - bTime;
		},
		render: date => dayjs(date).format("MM/DD/YYYY")
	},
	{
		title: "STATUS",
		dataIndex: "device_status",
		sorter: (a: any, b: any) => (a.device_status || "").localeCompare(b.device_status || ""),
		render: (_, device) => (
			<StatusIndicator deviceStatus={getDeviceStatusKey(device)} label={getDeviceStatusLabel(device)} />
		)
	},
	{
		dataIndex: "id",
		render: id => <Link to={routeNames.INSTALLED_DEVICES_DETAILS.replace(":installedDevId", id)}>More Info</Link>
	}
];

const filterFormatPGuides = pg =>
	pg.reduce(
		(acc, { guide }) => {
			if (guide) {
				acc.push({
					id: guide.id,
					name: guide.title,
					type: AttachmentType.GUIDE
				});
			}
			return acc;
		},
		[] as Array<{ id: any; name: string; type: AttachmentType }>
	);

const InstalledDeviceDetails: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();
	const params = useParams<{ installedDevId?: string; deviceId?: string }>();
	const navigate = useNavigate();
	const { pathname, state: locationState } = useLocation();
	const { theme } = useBrand();
	const canUserEdit = [UserRoleValue.admin_user, UserRoleValue.vendor_manager, UserRoleValue.vendor_employee].includes(
		loggedUserValue.role as UserRoleValue
	);

	useEffect(() => {
		// handle old url redirect
		if (/installed-products/.test(pathname)) {
			navigate(routeNames.INSTALLED_DEVICES_DETAILS.replace(":installedDevId", params.installedDevId!), {
				replace: true
			});
		}
	}, []);

	const setCategoryFilters = useInstalledProductsFiltersStore(state => state.setCategoryFilters);
	const { updateAiChatStatus } = useAiChatStatus();
	const match = matchPath(routeNames.ADMIN_INSTALLED_DEVICES_DETAILS, pathname);
	const isAdmin = match !== null;
	const [installedProduct, setInstalledProduct] = useState<any>(null);
	const [fileViewer, setFileViewer] = useState<{
		type: MediaType | null;
		src: string;
	}>({ type: null, src: "" });
	const [isLoadingAttachment, setIsLoadingAttachment] = useState<boolean>(false);
	const [productLocationsMap, setProductLocationsMap] = useState<Map<string, any[]>>(new Map());
	const [propertyOptions, setPropertyOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [selectedPropertyFilter, setSelectedPropertyFilter] = useState<string[]>([]);
	const [propertiesDropdownOpen, setPropertiesDropdownOpen] = useState<boolean>(false);
	const [selectedTypeFilter, setSelectedTypeFilter] = useState<string[]>([]);
	const [typeDropdownOpen, setTypeDropdownOpen] = useState<boolean>(false);
	// Defaults to [] (show all). When arriving from the dashboard badge click,
	// locationState.filterByStatus pre-selects Online + Offline. locationState is
	// ephemeral — cleared by the browser on refresh/direct load — so this resets
	// naturally without any extra logic.
	const [selectedStatusFilter, setSelectedStatusFilter] = useState<string[]>(
		locationState?.filterByStatus ? ["online", "offline"] : []
	);
	const [statusDropdownOpen, setStatusDropdownOpen] = useState<boolean>(false);
	const [collapseLocationActiveKeys, setCollapseLocationActiveKeys] = useState<string[]>(
		locationState?.openLocations ? ["1"] : []
	);
	const [collapseDeviceInfoActiveKeys, setCollapseDeviceInfoActiveKeys] = useState<string[]>(["1"]);
	const collapseLocationRef = useRef<HTMLDivElement>(null);
	const collapseDeviceInfoRef = useRef<HTMLDivElement>(null);
	const deviceActivityRef = useRef<HTMLDivElement>(null);

	const handleOnOpenPDF = useCallback(async (attachment: any) => {
		setIsLoadingAttachment(true);
		if (attachment.document_link) {
			try {
				setFileViewer({
					type: MediaType.Document,
					src: attachment.document_link
				});
				setIsLoadingAttachment(false);
			} catch (error) {
				console.log("handleOnOpenPDF error: ", error);
			}
		}
	}, []);

	const handleOnOpenVideo = useCallback((attachment: any) => {
		if (attachment.video_link) {
			setFileViewer({ type: MediaType.Video, src: attachment.video_link });
		}

		if (attachment.photo_link) {
			setFileViewer({ type: MediaType.Image, src: attachment.photo_link });
		}
	}, []);

	const handleOnCompleteGetInstalledProductById = useCallback((data: any) => {
		// console.log("Complete Get Installed data: ", data);
		setInstalledProduct(data.installed_products_by_pk[0]);
	}, []);

	const handleOnErrorGetInstalledProductById = useCallback(error => {
		console.log("useGetInstalledProductByIdQuery Error: ", error);
	}, []);

	const { loading, error } = useGetInstalledProductByIdQuery({
		skip: !params.installedDevId,
		variables: {
			id: params.installedDevId,
			is_not_deleted: true
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorGetInstalledProductById,
		onCompleted: handleOnCompleteGetInstalledProductById
	});

	const handleOnCompleteGetInstalledProductsByProductId = useCallback((data: any) => {
		console.log("Complete Get Installed Products By Product Id data: ", data);
		const separatedProducts = new Map([["ALL", [] as any]]);
		const uniqueProperties = new Map();
		const deviceImg = data?.products_by_pk?.photo_link;

		data?.products_by_pk?.installed_products.forEach(device => {
			if (device.property) {
				if (!separatedProducts.has(device.property.id)) {
					separatedProducts.set(device.property.id, []);
				}
				const deviceWithSetImg = {
					...device,
					photo_link: device.photo_link || deviceImg
				};
				separatedProducts.get(device.property.id)!.push(deviceWithSetImg);
				separatedProducts.get("ALL").push(deviceWithSetImg);
				uniqueProperties.set(device.property.id, device.property.name);
			}
		});

		const newPropertyOptions = Array.from(uniqueProperties.entries()).map(([id, name]) => ({
			value: id,
			label: name
		}));

		setProductLocationsMap(separatedProducts);
		setPropertyOptions(newPropertyOptions);
	}, []);

	const { data: installedDevicesData, loading: installedDevicesLoading } = useGetInstalledDevicesByDeviceIdQuery({
		skip: !params.deviceId,
		variables: {
			device_id: params.deviceId,
			property_ids: loggedUserValue.user_properties.map(up => up.property_id)
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorGetInstalledProductById,
		onCompleted: handleOnCompleteGetInstalledProductsByProductId
	});

	const filterByInstalledProductType = useCallback(
		(productType?: string) => {
			if (productType) {
				setCategoryFilters([productType]);
			} else {
				setCategoryFilters([]);
			}
			navigate(routeNames.INSTALLED_DEVICES);
		},
		[setCategoryFilters, navigate]
	);

	const handleOnClickAI = useCallback(() => {
		if (installedProduct) {
			const productName = installedProduct.product?.name ? installedProduct.product.name : "";

			const propertyName = installedProduct.property?.name ? installedProduct.property.name : "";

			const topic = `I understand that you may have a question related to the ${productName} at ${propertyName}.`;

			updateAiChatStatus({
				...aiChatStatusInitialValue,
				chatOrigin: AiChatOrigin.SPECIALTY,
				installed_product_id: installedProduct.id,
				chatTopic: topic
			});
		} else if (installedDevicesData?.products_by_pk) {
			const { name: productName, installed_products } = installedDevicesData.products_by_pk;

			const topic = `I understand that you may have a question related to the ${productName} at your properties.`;

			const selected_installed_products_ids: string[] = [];

			if (installed_products && installed_products.length) {
				installed_products.forEach(element => {
					if (element.id) {
						selected_installed_products_ids.push(element.id);
					}
				});
			}

			updateAiChatStatus({
				...aiChatStatusInitialValue,
				chatOrigin: AiChatOrigin.SPECIALTY,
				chatTopic: topic,
				selected_installed_products_ids
			});
		}

		navigate(routeNames.AI_CHAT);
	}, [installedProduct, installedDevicesData, updateAiChatStatus]);

	const handleOnEditProduct = useCallback(() => {
		if (installedProduct?.id) {
			const path = `${routeNames.ADMIN_INSTALLED_DEVICE_EDIT.replace(":installedDevId", installedProduct.id)}`;

			navigate(path);
		} else if (installedDevicesData?.products_by_pk?.id) {
			const path = `${routeNames.ADMIN_DEVICES_EDIT.replace(":deviceId", installedDevicesData.products_by_pk.id)}`;

			navigate(path);
		}
	}, [installedProduct?.id, installedDevicesData]);

	const handleOnClickGuide = useCallback(() => {
		const path = `${routeNames.GUIDE_ADD}`;

		navigate(path);
	}, [navigate]);

	const handlePropertySelection = useCallback(
		(propertyId: string) => {
			if (propertyId === "property-dropdown-option-all") {
				setSelectedPropertyFilter([]);
			} else {
				setSelectedPropertyFilter(prev => {
					if (prev.includes(propertyId)) {
						return prev.filter(id => id !== propertyId);
					} else {
						return [...prev, propertyId];
					}
				});
			}
		},
		[setSelectedPropertyFilter]
	);

	const handleStatusSelection = useCallback((statusKey: string) => {
		if (statusKey === "location-status-dropdown-option-all") {
			setSelectedStatusFilter([]);
		} else {
			setSelectedStatusFilter(prev => {
				if (prev.includes(statusKey)) {
					return prev.filter(s => s !== statusKey);
				} else {
					return [...prev, statusKey];
				}
			});
		}
	}, []);

	const handleTypeSelection = useCallback((type: string) => {
		if (type === "type-dropdown-option-all") {
			setSelectedTypeFilter([]);
		} else {
			setSelectedTypeFilter(prev => {
				if (prev.includes(type)) {
					return prev.filter(t => t !== type);
				} else {
					return [...prev, type];
				}
			});
		}
	}, []);

	const [propertiesOptions, propertyDropdownLabel = "All Properties"] = useMemo(() => {
		const allPropertiesSelected =
			selectedPropertyFilter.length === 0 || selectedPropertyFilter.length === propertyOptions.length;
		let selectedPropertyName;

		if (propertyOptions.length) {
			const productProperties: MenuProps["items"] = propertyOptions.reduce(
				(options, property, currentindex) => {
					const optionId = `property-dropdown-option-${currentindex + 1}-${property.value}`;

					const isSelected = selectedPropertyFilter.includes(property.value);

					if (!selectedPropertyName && isSelected) {
						// property should be defined by this step, if not, maybe refactor to ensure it is
						selectedPropertyName = property.label;
					}

					const newOption = {
						key: `${property.value}`,
						icon: (
							<div>
								<input
									className="custom-checkbox"
									id={optionId}
									type="checkbox"
									onClick={e => e.stopPropagation()}
									checked={isSelected}
									onChange={voidFnc}
								/>
								<label htmlFor={optionId} className="custom-checkbox-label">
									{property.label}
								</label>
							</div>
						)
					};

					options.push(newOption);

					return options;
				},
				[
					{
						key: "property-dropdown-option-all",
						icon: (
							<div>
								<input
									className="custom-checkbox"
									id="property-dropdown-option-all"
									type="checkbox"
									onClick={e => e.stopPropagation()}
									checked={allPropertiesSelected}
									onChange={voidFnc}
								/>
								<label htmlFor="property-dropdown-option-all" className="custom-checkbox-label">
									All Properties
								</label>
							</div>
						)
					}
				]
			);

			return [productProperties, createLabelForFilters("All Properties", selectedPropertyFilter, selectedPropertyName)];
		}

		return [[], "All Properties"];
	}, [propertyOptions, selectedPropertyFilter]);

	const [statusOptions, statusDropdownLabel = "All Statuses"] = useMemo(() => {
		const allSelected = selectedStatusFilter.length === 0;
		const firstSelectedLabel = DEVICE_STATUS_FILTER_OPTIONS.find(o => selectedStatusFilter.includes(o.key))?.label;

		const items = buildCheckboxMenuItems(
			DEVICE_STATUS_FILTER_OPTIONS,
			selectedStatusFilter,
			{ key: "location-status-dropdown-option-all", label: "All Statuses", checked: allSelected },
			"location-status-dropdown-option"
		);

		return [items, createLabelForFilters("All Statuses", selectedStatusFilter, firstSelectedLabel)];
	}, [selectedStatusFilter]);

	const featureFlagGuideEnabled = process.env.REACT_APP_FF_GUIDE_ENABLED == "true";

	const filteredInstallDevicesData = useMemo(() => {
		let data: any[] | undefined;
		if (selectedPropertyFilter.length === 0 || selectedPropertyFilter.length === propertyOptions.length) {
			data = productLocationsMap.get("ALL");
		} else {
			data = selectedPropertyFilter.reduce((acc, curr) => {
				const productLocation = productLocationsMap.get(curr);
				if (productLocation) {
					acc = acc.concat(productLocation);
				}
				return acc;
			}, [] as any[]);
		}
		if (selectedStatusFilter.length) {
			const nonNullStatuses = selectedStatusFilter.filter(s => s !== "null");
			const includesNull = selectedStatusFilter.includes("null");
			data = data?.filter((d: any) => {
				if (nonNullStatuses.includes(d.device_status)) return true;
				if (includesNull && d.device_status == null) return true;
				return false;
			});
		}
		return data;
	}, [productLocationsMap, selectedPropertyFilter, selectedStatusFilter]);

	const values = useMemo(() => {
		if (installedProduct) {
			const {
				id,
				installation_date,
				warranty_status,
				product_serial = "",
				location,
				building,
				floor,
				room,
				product,
				product_notes,
				property,
				installed_product_attachments = [],
				photo_link,
				partner_disconnected_at,
				partner_association,
				device_logs = []
			} = installedProduct;

			const {
				id: product_id,
				name = "",
				description = "",
				product_attachments = [],
				model = "",
				make = "",
				photo_link: product_photo_link = "",
				product_guides = []
			} = product || {};

			const guidesFilteredAndFormatted = filterFormatPGuides(product_guides);

			const attachments: Array<any> = [
				...guidesFilteredAndFormatted,
				...installed_product_attachments,
				...product_attachments
			];

			return {
				id,
				type: product?.product_category || { label: "", name: "" },
				product,
				name,
				description,
				model,
				make,
				property,
				product_attachments: attachments,
				product_notes,
				productImg: photo_link || product_photo_link || "",
				product_serial: product_serial,
				property_name: property?.name,
				location: location,
				building_name: building?.name,
				floor_name: floor?.name,
				room_name: room?.name,
				status: `WARRANTY ${warranty_status}`,
				installation_date: `${dayjs(installation_date).format("MM/DD/YYYY")}`,
				product_id,
				latestDeviceLog: device_logs[0],
				partner_disconnected_at,
				partner_association
			};
		}

		if (installedDevicesData?.products_by_pk) {
			const {
				id,
				name,
				description,
				make,
				model,
				product_attachments = [],
				photo_link,
				product_notes,
				product_guides = []
			} = installedDevicesData.products_by_pk;

			const guidesFilteredAndFormatted = filterFormatPGuides(product_guides);

			return {
				product_id: id,
				type: installedDevicesData.products_by_pk.product_category || { label: "", name: "" },
				name,
				description,
				model,
				make,
				product_attachments: [...guidesFilteredAndFormatted, ...product_attachments],
				product_notes,
				productImg: photo_link || ""
			};
		}

		return {
			type: { label: "", name: "" },
			name: "",
			description: "",
			make: "",
			model: "",
			product_attachments: [],
			productImg: "",
			product_serial: "",
			product_notes: "",
			property_name: "",
			location: "",
			status: `WARRANTY`,
			installation_date: ""
		};
	}, [installedProduct, installedDevicesData]);

	const distinctAttachmentTypes = useMemo(() => {
		const types = values.product_attachments.map((a: any) => a.type).filter(Boolean);
		return ([...new Set(types)] as string[]).sort((a, b) => a.localeCompare(b));
	}, [values.product_attachments]);

	const [typeDropdownOptions, typeDropdownLabel = "All Resources"] = useMemo(() => {
		const allSelected = selectedTypeFilter.length === 0 || selectedTypeFilter.length === distinctAttachmentTypes.length;
		let selectedTypeName: string | undefined;

		if (distinctAttachmentTypes.length) {
			const items: MenuProps["items"] = distinctAttachmentTypes.reduce(
				(options, type, currentIndex) => {
					const optionId = `type-dropdown-option-${currentIndex + 1}-${type}`;
					const isSelected = selectedTypeFilter.includes(type);

					if (!selectedTypeName && isSelected) {
						selectedTypeName = AttachmentTypePluralizedDisplayNames[type as AttachmentType] ?? type;
					}

					options.push({
						key: type,
						icon: (
							<div>
								<input
									className="custom-checkbox"
									id={optionId}
									type="checkbox"
									onClick={e => e.stopPropagation()}
									checked={isSelected}
									onChange={voidFnc}
								/>
								<label htmlFor={optionId} className="custom-checkbox-label">
									{AttachmentTypePluralizedDisplayNames[type as AttachmentType] ?? type}
								</label>
							</div>
						)
					});

					return options;
				},
				[
					{
						key: "type-dropdown-option-all",
						icon: (
							<div>
								<input
									className="custom-checkbox"
									id="type-dropdown-option-all"
									type="checkbox"
									onClick={e => e.stopPropagation()}
									checked={allSelected}
									onChange={voidFnc}
								/>
								<label htmlFor="type-dropdown-option-all" className="custom-checkbox-label">
									All Resources
								</label>
							</div>
						)
					}
				] as NonNullable<MenuProps["items"]>
			);

			return [items, createLabelForFilters("All Resources", selectedTypeFilter, selectedTypeName)];
		}

		return [[], "All Resources"];
	}, [distinctAttachmentTypes, selectedTypeFilter]);

	const filteredAttachments = useMemo(() => {
		const attachments: any[] =
			selectedTypeFilter.length === 0 || selectedTypeFilter.length === distinctAttachmentTypes.length
				? [...values.product_attachments]
				: values.product_attachments.filter((a: any) => selectedTypeFilter.includes(a.type));

		return attachments.sort((a, b) => {
			const typeCompare = (a.type ?? "").localeCompare(b.type ?? "");
			if (typeCompare !== 0) return typeCompare;
			return (a.name ?? "").localeCompare(b.name ?? "");
		});
	}, [values.product_attachments, selectedTypeFilter, distinctAttachmentTypes]);

	const handleScrollToActivity = useCallback(() => {
		deviceActivityRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	}, []);

	const handlePropertyClick = useCallback(
		val => {
			// Ensure the collapse is open and scroll to it
			setSelectedPropertyFilter([val]);
			setCollapseLocationActiveKeys(["1"]);
			setTimeout(() => {
				if (collapseLocationRef.current) {
					collapseLocationRef.current.scrollIntoView({
						behavior: "smooth",
						block: "start"
					});
				}
			}, 100);
		},
		[collapseLocationRef]
	);

	const installedDevicePropertySplit = useMemo(() => {
		return (
			<div className="installed-device-property-split">
				{propertyOptions.map(option => {
					const installedCount = productLocationsMap.get(option.value)?.length || 0;

					if (installedCount === 0) return null;

					return (
						<div
							key={option.value}
							className="product-d-data device-d-data-property-count"
							onClick={() => handlePropertyClick(option.value)}
						>
							{`${option.label} (${installedCount})`}
						</div>
					);
				})}
			</div>
		);
	}, [installedDevicesData, propertyOptions]);

	return (
		<div className="products-details-screen-container">
			{(installedProduct || installedDevicesData?.products_by_pk) && (
				<Breadcrumb
					className="products-breadcrumbs"
					items={
						isAdmin
							? [
									{
										title: "Properties",
										onClick: () => {
											navigate(routeNames.ADMIN_PROPERTIES);
										}
									},
									...(values.property?.name
										? [
												{
													title: values.property.name,
													onClick: () => {
														if (values.property?.id) {
															const path = `${routeNames.ADMIN_PROPERTY_DETAILS.replace(":propertyId", values.property.id)}`;
															navigate(path);
														}
													}
												}
											]
										: []),
									...(values.name
										? [
												{
													title: values.name
												}
											]
										: [])
								]
							: [
									{
										title: "My Devices",
										onClick: () => filterByInstalledProductType()
									},
									...(values.type?.label
										? [
												{
													title: values.type.label,
													onClick: () => filterByInstalledProductType(values.type.name)
												}
											]
										: []),
									...(values.name
										? [
												{
													title: values.name,
													...(params.installedDevId && {
														onClick: () =>
															navigate(
																routeNames.INSTALLED_DEVICE_DETAILS.replace(":deviceId", installedProduct?.product_id)
															)
													})
												}
											]
										: []),
									...(params.installedDevId
										? [
												{
													title: "Details"
												}
											]
										: [])
								]
					}
				/>
			)}
			<div className="product-details-container">
				<div className="product-title-container">
					<span className="product-d-name">{values.name}</span>
					<div className="product-d-warranty-container">
						{values.id && (
							<div className="product-device-status-container">
								{/* The last log of a disconnected device still says "offline" or "online", but
								    that status is old, so the disconnected status is shown instead. */}
								{values.partner_disconnected_at ? (
									<StatusIndicator deviceStatus={getDeviceStatusKey(values)} label={getDeviceStatusLabel(values)} />
								) : (
									values.latestDeviceLog && <StatusIndicator deviceStatus={values.latestDeviceLog.event_state} />
								)}
								<button className="product-device-activity-button" onClick={handleScrollToActivity}>
									Activity
								</button>
							</div>
						)}
						{canUserEdit && (
							<img
								alt={`edit-attachment-icon`}
								src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
								className="selected-file-icon"
								onClick={handleOnEditProduct}
							/>
						)}
						<div className="product-d-warranty">
							<span>{values.status}</span>
						</div>
					</div>
				</div>

				<div className="product-d-row">
					<div className="product-d-image-container">
						{values.productImg ? (
							<img src={values.productImg} className={`product-d-image`} />
						) : (
							<div className={`product-image`}>
								<img
									src={`${process.env.PUBLIC_URL}/assets/images/icons/product-placeholder.svg`}
									className={`product-image-placeholder`}
								/>
							</div>
						)}
					</div>
					<div className="product-descriptions-container">
						<span className="product-d-name">Device Information</span>
						<div className="product-d-data-container">
							{values.make && <span className="product-d-data">{`Make: ${values.make}`}</span>}
							{values.model && <span className="product-d-data">{`Model: ${values.model}`}</span>}
							{values.installation_date && (
								<span className="product-d-data">{`Installation Date: ${values.installation_date}`}</span>
							)}
							{values.product_serial && <span className="product-d-data">{`Serial: ${values.product_serial}`}</span>}
							{values.property_name && <span className="product-d-data">{`Property: ${values.property_name}`}</span>}
							{values.location && <span className="product-d-data">{`Location: ${values.location}`}</span>}
							{values.building_name && <span className="product-d-data">{`Building: ${values.building_name}`}</span>}
							{values.floor_name && <span className="product-d-data">{`Floor: ${values.floor_name}`}</span>}
							{values.room_name && <span className="product-d-data">{`Room/Area: ${values.room_name}`}</span>}
						</div>

						{params.deviceId && (
							<Collapse
								ref={collapseDeviceInfoRef}
								className="installed-device-property-split-table"
								activeKey={collapseDeviceInfoActiveKeys}
								onChange={setCollapseDeviceInfoActiveKeys}
								ghost
								items={[
									{
										key: "1",
										label: `${
											installedDevicesData?.products_by_pk?.installed_products.length
										} at ${propertyOptions.length} ${propertyOptions.length > 1 ? "properties" : "property"}`,
										children: installedDevicePropertySplit
									}
								]}
								expandIcon={({ isActive }) => <DropdownArrow isActive={isActive} />}
								expandIconPosition="end"
							/>
						)}

						{values.description && <p className="product-d-data description">{values.description}</p>}
						{values.product_notes && (
							<>
								<span className="product-d-name">Notes</span>
								<span className="product-d-data">{values.product_notes}</span>
							</>
						)}

						<div className="product-d-cta-container">
							{featureFlagGuideEnabled && (
								<div className="highnote-button-white-wrapper">
									<Link to={routeNames.GUIDE_ADD} className="create-guide-button">
										<img src={theme.buttonStarImage} alt="star" />
										Create a Guide
									</Link>
								</div>
							)}

							{!isAdmin && (
								<button className="highnote-button" onClick={handleOnClickAI}>
									Ask a question about this
								</button>
							)}
						</div>
					</div>
				</div>

				{params.deviceId && (
					<>
						<div className="product-d-separator"></div>

						<Collapse
							ref={collapseLocationRef}
							className="device-d-table-collapse"
							activeKey={collapseLocationActiveKeys}
							onChange={setCollapseLocationActiveKeys}
							size="large"
							items={[
								{
									key: "1",
									label: "Locations",
									children: (
										<div className="device-d-table-container">
											<div className="products-filters-row">
												<Dropdown
													menu={{
														items: propertiesOptions,
														selectable: true,
														multiple: true,
														onSelect: ({ key }) => handlePropertySelection(key),
														onDeselect: ({ key }) => handlePropertySelection(key)
													}}
													trigger={["click"]}
													open={propertiesDropdownOpen}
													onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
														if (info.source === "trigger" && open === false) {
															setPropertiesDropdownOpen(false);
														}
													}}
													placement="bottomLeft"
													className="products-dropdown"
													overlayClassName="products-dropdown-overlay"
													arrow
													disabled={propertiesOptions.length === 0}
												>
													<Button
														onClick={() => {
															setPropertiesDropdownOpen(previousValue => !previousValue);
														}}
													>
														{propertyDropdownLabel}
														<img
															src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
															title={propertyDropdownLabel}
															className="chevron-button"
														/>
													</Button>
												</Dropdown>
												<Dropdown
													menu={{
														items: statusOptions,
														selectable: true,
														multiple: true,
														onSelect: ({ key }) => handleStatusSelection(key),
														onDeselect: ({ key }) => handleStatusSelection(key)
													}}
													trigger={["click"]}
													open={statusDropdownOpen}
													onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
														if (info.source === "trigger" && open === false) {
															setStatusDropdownOpen(false);
														}
													}}
													placement="bottomLeft"
													className="products-dropdown"
													overlayClassName="products-dropdown-overlay"
													arrow
												>
													<Button
														onClick={() => {
															setStatusDropdownOpen(previousValue => !previousValue);
														}}
													>
														{statusDropdownLabel}
														<img
															src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
															title={statusDropdownLabel}
															className="chevron-button"
														/>
													</Button>
												</Dropdown>
											</div>
											<Table
												className="device-d-table"
												columns={locationsTableColumns}
												dataSource={filteredInstallDevicesData}
												pagination={false}
												loading={installedDevicesLoading}
												rowKey="id"
												onRow={record => ({
													style: { cursor: "pointer" },
													onClick: () =>
														navigate(routeNames.INSTALLED_DEVICES_DETAILS.replace(":installedDevId", record.id))
												})}
											/>
										</div>
									)
								}
							]}
							expandIcon={({ isActive }) => <DropdownArrow isActive={isActive} />}
							expandIconPosition="end"
						/>
					</>
				)}

				{values.product_attachments.length > 0 && (
					<>
						<div className="product-d-separator"></div>

						<div className="product-d-attachments-section">
							<Dropdown
								menu={{
									items: typeDropdownOptions,
									selectable: true,
									multiple: true,
									onSelect: ({ key }) => handleTypeSelection(key),
									onDeselect: ({ key }) => handleTypeSelection(key)
								}}
								trigger={["click"]}
								open={typeDropdownOpen}
								onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
									if (info.source === "trigger" && open === false) {
										setTypeDropdownOpen(false);
									}
								}}
								placement="bottomLeft"
								className="products-dropdown"
								overlayClassName="products-dropdown-overlay"
								arrow
								disabled={distinctAttachmentTypes.length === 0}
							>
								<Button
									onClick={() => {
										setTypeDropdownOpen(previousValue => !previousValue);
									}}
								>
									{typeDropdownLabel}
									<img
										src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
										title={typeDropdownLabel}
										className="chevron-button"
									/>
								</Button>
							</Dropdown>

							<div className="product-d-attachments">
								{filteredAttachments.map((attachment: any) => (
									<DeviceAttachment
										key={attachment.id}
										attachment={attachment}
										onOpenPDF={handleOnOpenPDF}
										onOpenVideo={handleOnOpenVideo}
									/>
								))}
							</div>
						</div>
					</>
				)}

				{values.id && (
					<div ref={deviceActivityRef}>
						<div className="product-d-separator"></div>
						<DeviceStatusActivity installedProductId={values.id} />
					</div>
				)}

				<div className="product-d-separator"></div>

				<ProductDetailsFeedback
					productName={values.name}
					installedProductId={values.id}
					productId={values.product_id}
				/>
			</div>

			<MediaPlayer
				onCancel={() => setFileViewer({ type: null, src: "" })}
				src={fileViewer.src}
				open={!!fileViewer.src && fileViewer.type !== null}
				mediaType={fileViewer.type}
			/>
		</div>
	);
};

export default InstalledDeviceDetails;
